import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Col, Row } from 'react-flexbox-grid'

import { ExpandableBlocks, ContentBlockLoading } from 'components/ContentBlock'
import DxDataTable from 'components/DxDataTable'

import Filter from './Filter'
import { columns_inventory, columns_inventoryDetails } from './columns'

import { loadAppConstants } from 'gql/query'
import { getOrderTypeExportOptions } from 'sections/options/sections/GlobalCashRegisters/pages/ERP/orderTypeExportOptions'

const query = gql`
query getStatisticsStorage($params: StatisticsStorageParams!){
	cashAssist {
		getStatisticsStorage(params: $params) {
			id
			name
			code
			amount
			unit
			inventoryDifference
			amountBeforeLastInventory
		}
	}
}
`
const queryDetails = gql`
query getStatisticsStorageDetails($params: StatisticsStorageDetailsParams!) {
	cashAssist {
		getStatisticsStorageDetails(params: $params) {
			cashRegisterName
			type
			number
			date
			plus
			minus
			amount
			parentProductId
			parentDirection
			parentProduct {
				id
				name
			}
		}
	}
}
`

const Inventory = () => {
	const [expandedId, setExpandedId] = useState(null)
	const [selectedProduct, setSelectedProduct] = useState(null)
	const [filter, setFilter] = useState({})
	const [t, i18n] = useTranslation()
	const [getStatistics, { data, loading, called }] = useLazyQuery(query, {
		fetchPolicy: 'network-only'
	})
	const { data: salesUnitData } = useQuery(loadAppConstants, {
		variables: { type: 34, language: i18n.language }
	})

	const inventoryDetails = useQuery(queryDetails, {
		variables: {
			params: {
				cashRegisterIds: filter.cashRegisterIds,
				date: filter.date,
				orderTypeId: filter.orderTypeId,
				productId: selectedProduct?.id,
				amountBeforeLastInventory: selectedProduct?.amountBeforeLastInventory
			}
		},
		skip: !selectedProduct
	})
	const orderTypeExportOptions = getOrderTypeExportOptions(t)
	const orederTypesMap = orderTypeExportOptions.reduce((acc, opt) => {
		acc[opt.value] = opt.label
		acc[String(opt.value)] = opt.label
		return acc
	}, {
		'-1': t('common.strings.bill'),
		'-2': t('statistic_section.strings.stock_before_last_inventory')
	})

	const salesUnitsMap = salesUnitData?.cashAssist?.loadAppConstants?.reduce((acc, item) => {
		acc[item.value] = item.label
		return acc
	}, {})

	const handleSetFilter = (filterParams) => {
		setExpandedId('1')
		setFilter(filterParams)
		setSelectedProduct(null)
		getStatistics({ variables: { params: filterParams } })
	}

	return(
		<ExpandableBlocks expandedId={expandedId} onExpand={setExpandedId}>
			<Filter handleSetFilter={handleSetFilter} />
			{called &&
				<ContentBlockLoading
					isLoading={loading}
					topBorder
					alwaysExpanded
					blockId='1'
				>
					<Row>
						<Col xs={6}>
							<DxDataTable
								label={t('statistic_section.sub_sections.inventory')}
								columns={columns_inventory}
								withSearch
								height={508}
								excelExport
								data={data?.cashAssist?.getStatisticsStorage?.map(item => ({
									...item,
									unitName: salesUnitsMap[item.unit] || ''
								}))}
								onRowClick={rowData => setSelectedProduct(rowData)}
							/>
						</Col>
						{selectedProduct && <Col xs={6}>
							<DxDataTable
								label={t('common.strings.product')}
								columns={columns_inventoryDetails({ isInteger: selectedProduct.unit === '1' })} // unit by piece
								loading={inventoryDetails.loading}
								withSearch={false}
								height={508}
								excelExport
								data={inventoryDetails.data?.cashAssist?.getStatisticsStorageDetails.map(item => {
									const out = {
										...item,
										type: orederTypesMap[item.type] || ''
									}
									// this is needed to exclude special -2 type row with amountBeforeLastInventory from total count
									if(item.type === '-2') {
										out.plusDisplay = out.plus
										out.plus = null
										out.minusDisplay = out.minus
										out.minus = null
									}
									return out
								})}
							/>
						</Col>}
					</Row>
				</ContentBlockLoading>
			}
		</ExpandableBlocks>
	)
}

export default Inventory
